import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementSettingsService } from 'src/app/services/api/disbursement/disbursement-settings.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DepartmentDisbursementSettingRow,
  DisbursementDefaultSettingModel,
  DisbursementTermSettingModel,
  DisbursementTermSettingPayload,
} from 'src/app/services/models/disbursement/disbursement-settings.model';
import { DisbursementUser } from 'src/app/services/models/disbursement/disbursement.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import {
  buildAcademicTermNameOptions,
  buildAcademicYearOptions,
  currentAcademicTerm,
  findAcademicTerm,
  toBuddhistYear,
} from 'src/utils/academic-term-options';

const CATCH_UP_MIN_GAP_DAYS = 14;

const TERM_SAVE_CONFIRM_MESSAGE =
  'ยืนยันการบันทึกวันที่ส่งเบิกของเทอมนี้หรือไม่ ?\nโดยวันที่ส่งเบิกนี้จะใช้ร่วมกันทั้งคณะและทุกสาขาวิชา';
const TERM_SAVE_CONFIRM_MESSAGE_OPENS_ROUND =
  'ยืนยันการบันทึกวันที่ส่งเบิกของเทอมนี้หรือไม่ ?\nวันที่ที่เลือกคือวันนี้ — ระบบจะเริ่มรอบเบิกจ่ายของเทอมนี้ให้อาจารย์ทั้งคณะทันทีเมื่อกดยืนยัน และแก้ไขวันที่ส่งเบิกไม่ได้อีก';
const DEFAULT_SAVE_CONFIRM_MESSAGE =
  'ค่านี้เป็นค่าตั้งต้นเมื่อกด "แก้ไข" ของแต่ละสาขาวิชาเท่านั้น \nและจะไม่ทับค่าที่สาขาวิชาบันทึกไว้แล้ว ต้องการบันทึกข้อมูลหรือไม่?';

type TermDateControlName = 'disbursementDate' | 'catchUpDate' | 'endDate';

// Validators.required accepts '   ', but the payload is trimmed before it is sent — so a
// whitespace-only value would reach the API as blank and come back 422.
const nonBlank = (control: AbstractControl) =>
  String(control.value ?? '').trim().length > 0 ? null : { required: true };

type SettingControlName = 'planName' | 'projectName' | 'expenseCategory' | 'financeOfficerUserId';

const SETTING_REQUIRED_MESSAGES: Record<SettingControlName, string> = {
  planName: 'กรุณาระบุแผนงาน',
  projectName: 'กรุณาระบุงาน/โครงการ',
  expenseCategory: 'กรุณาระบุหมวดรายจ่าย',
  financeOfficerUserId: 'กรุณาเลือกเจ้าหน้าที่การเงิน',
};

// Rails renders `full_messages`, so a field error arrives with its English attribute name in
// front of the Thai copy — that prefix is the only thing tying a message back to a control.
const TERM_DATE_ERROR_PREFIXES: { prefix: string; control: TermDateControlName }[] = [
  { prefix: 'Catch up date', control: 'catchUpDate' },
  { prefix: 'End date', control: 'endDate' },
  { prefix: 'Disbursement date', control: 'disbursementDate' },
];

// The three date fields report into one block under the save button, so each message has
// to name its own field — "ต้องเป็นวันหลังวันที่ส่งเบิก" on its own says nothing about which.
const TERM_DATE_LABELS: { control: TermDateControlName; label: string }[] = [
  { control: 'disbursementDate', label: 'วันที่ส่งเบิก' },
  { control: 'catchUpDate', label: 'วันเริ่มรอบเก็บตก' },
  { control: 'endDate', label: 'วันที่สิ้นสุดการส่งเบิก' },
];

interface DepartmentSettingTableRow {
  departmentId: number;
  departmentName: string;
  planName: string;
  projectName: string;
  expenseCategory: string;
  financeOfficerName: string;
  raw: DepartmentDisbursementSettingRow;
}

interface DepartmentSettingDataSource {
  data: DepartmentSettingTableRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-disbursement-plan',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './disbursement-plan.component.html',
  styleUrl: './disbursement-plan.component.scss',
})
export class DisbursementPlanComponent implements OnInit, OnDestroy {
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<unknown>;

  @ViewChild('termSaveConfirmDialog')
  termSaveConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('defaultSaveConfirmDialog')
  defaultSaveConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('settingSaveConfirmDialog')
  settingSaveConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('actionSuccessDialog')
  actionSuccessDialog?: ConfirmDialogComponent;

  termForm = inject(FormBuilder).group({
    academicTermId: [null as number | null],
    academicTerm: [null as string | null],
    academicYear: [null as string | null],
    disbursementDate: [null as string | null, Validators.required],
    catchUpDate: [null as string | null],
    endDate: [null as string | null, Validators.required],
  });

  defaultForm = inject(FormBuilder).group({
    planName: [''],
    projectName: [''],
    expenseCategory: [''],
  });

  // ทุกช่องบังคับ (ADR-DK): แถวที่บันทึกไว้ไม่ครบจะถูกล็อกถาวรทันทีที่ถึงวันที่ส่งเบิก — context/decisions/DECISION_DisbursementV20.md
  settingForm = inject(FormBuilder).group({
    planName: ['', nonBlank],
    projectName: ['', nonBlank],
    expenseCategory: ['', nonBlank],
    financeOfficerUserId: [null as number | null, Validators.required],
  });

  columns: unknown[] = [];
  datasource = signal<DepartmentSettingDataSource>(this.createDataSource([]));
  tableLoading = signal(false);
  deptSettingsLocked = signal(false);
  academicTermNameOptions: string[] = [];
  academicYearOptions: string[] = [];

  termSetting = signal<DisbursementTermSettingModel | null>(null);
  termSettingLoading = signal(false);
  termSettingSaving = signal(false);
  termSaveConfirmMessage = signal(TERM_SAVE_CONFIRM_MESSAGE);

  defaultSetting = signal<DisbursementDefaultSettingModel | null>(null);
  defaultSettingSaving = signal(false);
  readonly defaultSaveConfirmMessage = DEFAULT_SAVE_CONFIRM_MESSAGE;

  financeOfficers = signal<DisbursementUser[]>([]);
  editDialogOpen = signal(false);
  successTitle = signal('');
  successMessage = signal('');
  editTarget = signal<DepartmentSettingTableRow | null>(null);
  settingSaving = signal(false);
  settingSaveConfirmMessage = signal('');

  private planRows: DepartmentDisbursementSettingRow[] = [];
  private currentPage = 1;
  private currentLimit = 50;
  private academicTerms: AcademicTermYearModel[] = [];
  private readonly subscriptions = new Subscription();
  private readonly settingsService = inject(DisbursementSettingsService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);

  // Feeds the picker's [minDate]: วันที่ส่งเบิก opens the faculty-wide sweep, so it can only
  // ever be today or later. A locked (already reached) date is disabled anyway, so this bound
  // never fights a stored past date.
  get minDisbursementDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  get termSettingLocked(): boolean {
    return this.termSetting()?.reached === true;
  }

  get catchUpLocked(): boolean {
    return this.termSetting()?.catch_up_reached === true;
  }

  // วันที่สิ้นสุดการส่งเบิก stays editable while the round is still running, so the form only
  // closes for good once every date of the term has gone by.
  get termSettingClosed(): boolean {
    return this.termSettingLocked && this.catchUpLocked && this.termSetting()?.ended === true;
  }

  ngOnInit(): void {
    this.initColumns();
    this.listenTermFilters();
    // ADR-DF: the department table is per-term — context/decisions/DECISION_DisbursementV20.md
    this.loadFilterOptions();
    this.loadFinanceOfficers();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Runs reseed + every validation layer WITHOUT saving — the opener calls this to decide
  // whether the confirm dialog may open at all, and onSaveTermSetting calls it again right
  // before the PATCH. Idempotent: reseed re-sets an already-stored value and setErrors/
  // markAsTouched are idempotent, so calling it twice for one click is safe.
  private prepareTermSave(): DisbursementTermSettingPayload | null {
    // A locked date is resent as stored, never as whatever the form is holding — a stale GET
    // or a timezone-shifted picker value would otherwise read as a change the user cannot undo.
    this.reseedLockedDates();

    const { academicTermId, disbursementDate, catchUpDate, endDate } = this.termForm.getRawValue();
    if (academicTermId == null) {
      return null;
    }

    if (disbursementDate == null || disbursementDate === '') {
      this.termForm.controls.disbursementDate.markAsTouched();
      return null;
    }

    // A reached (locked) วันที่ส่งเบิก IS in the past and is resent verbatim by
    // reseedLockedDates() above — only a date the user can still move has to be
    // forward-looking. The backend enforces the same rule on the changing date only.
    if (!this.termSettingLocked && this.isPastDay(disbursementDate)) {
      this.termForm.controls.disbursementDate.setErrors({ pastDate: true });
      this.termForm.controls.disbursementDate.markAsTouched();
      return null;
    }

    // A locked catch-up date can no longer be fixed, so checking its gap here would only
    // dead-end the save — the backend skips it too once the date is not the one changing.
    if (!this.catchUpLocked && catchUpDate != null && catchUpDate !== ''
      && !this.catchUpGapSatisfied(disbursementDate, catchUpDate)) {
      this.termForm.controls.catchUpDate.setErrors({ catchUpGap: true });
      this.termForm.controls.catchUpDate.markAsTouched();
      return null;
    }

    // วันที่สิ้นสุดการส่งเบิก ไม่เคยถูกล็อก จึงบังคับกรอกได้ทุกครั้งที่กดบันทึก ต่างจากอีกสองวัน
    // ที่ต้องส่งค่าเดิมกลับไปตอนถูกล็อก
    if (endDate == null || endDate === '') {
      this.termForm.controls.endDate.markAsTouched();
      return null;
    }

    if (!this.endDateAfterDisbursement(disbursementDate, endDate)) {
      this.termForm.controls.endDate.setErrors({ endDateOrder: true });
      this.termForm.controls.endDate.markAsTouched();
      return null;
    }

    // Every date goes out on every save: the backend only rejects one it sees actually
    // changing, and an omitted key made the "ถึงวันที่ส่งเบิกแล้ว" 422 unreachable.
    return {
      disbursement_date: disbursementDate,
      catch_up_date: catchUpDate,
      end_date: endDate,
    };
  }

  onOpenTermSaveConfirm(): void {
    if (this.termSettingSaving() || this.termSettingClosed) {
      return;
    }

    const payload = this.prepareTermSave();
    if (payload == null) {
      return;
    }

    this.termSaveConfirmMessage.set(
      this.opensRoundImmediately(payload.disbursement_date)
        ? TERM_SAVE_CONFIRM_MESSAGE_OPENS_ROUND
        : TERM_SAVE_CONFIRM_MESSAGE,
    );
    this.termSaveConfirmDialog?.open();
  }

  onSaveTermSetting(): void {
    if (this.termSettingSaving() || this.termSettingClosed) {
      return;
    }

    const payload = this.prepareTermSave();
    if (payload == null) {
      return;
    }

    // Saving a date that is already today/past starts the faculty-wide sweep right
    // away — say so instead of a plain "saved".
    const wasUnreached = this.termSetting()?.reached !== true;

    // prepareTermSave() only returns non-null once academicTermId is resolved, but it hands
    // back the payload alone — re-read it here rather than assert past the type.
    const academicTermId = this.termForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      return;
    }

    this.termSettingSaving.set(true);
    this.settingsService.updateTermSetting(academicTermId, payload).pipe(
      finalize(() => this.termSettingSaving.set(false)),
    ).subscribe({
      next: (setting) => {
        // The field-error block under the save button is invisible while the modal sits on
        // top of it — always close before reacting, on success as well as on error below.
        this.termSaveConfirmDialog?.close();
        const openedNow = wasUnreached && setting?.reached;
        this.applyTermSetting(setting);
        if (openedNow) {
          // The table's edit lock is backend state — re-read it so it engages without a reload.
          this.loadDepartmentSettings();
        }
        this.showSuccess(
          'บันทึกวันที่ส่งเบิกสำเร็จ',
          openedNow
            ? 'บันทึกแล้ว และเริ่มรอบเบิกจ่ายของเทอมนี้ทันที'
            : 'บันทึกการตั้งค่าการส่งเบิกเรียบร้อยแล้ว',
        );
      },
      // The form keeps what the user typed — re-reading the stored dates here would hide the
      // very value the backend just complained about.
      error: (err) => {
        this.termSaveConfirmDialog?.close();
        console.error(err);
        this.applyTermErrorMessages(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  // Mirrors the openedNow branch of the success modal above, but has to run BEFORE the save
  // (the opener needs it to pick which confirm copy to show).
  private opensRoundImmediately(disbursementDate: string | null | undefined): boolean {
    if (disbursementDate == null || this.termSetting()?.reached === true) {
      return false;
    }

    const day = this.toCalendarDay(disbursementDate);
    return day != null && day <= this.todayCalendarDay();
  }

  onOpenDefaultSaveConfirm(): void {
    if (this.defaultSettingSaving()) {
      return;
    }

    this.defaultSaveConfirmDialog?.open();
  }

  onSaveDefaultSetting(): void {
    if (this.defaultSettingSaving()) {
      return;
    }

    const value = this.defaultForm.getRawValue();
    this.defaultSettingSaving.set(true);
    this.settingsService.updateDefaultSetting({
      plan_name: String(value.planName ?? '').trim(),
      project_name: String(value.projectName ?? '').trim(),
      expense_category: String(value.expenseCategory ?? '').trim(),
    }).pipe(
      finalize(() => this.defaultSettingSaving.set(false)),
    ).subscribe({
      next: (setting) => {
        this.defaultSaveConfirmDialog?.close();
        // Prefill only — no department row changes, so the table stays as it is.
        this.applyDefaultSetting(setting);
        this.showSuccess('บันทึกค่าตั้งต้นสำเร็จ', 'บันทึกค่าตั้งต้นเรียบร้อยแล้ว');
      },
      error: (err) => {
        this.defaultSaveConfirmDialog?.close();
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  // One control carries several different failures — the message has to follow the error key,
  // otherwise every future error on วันเริ่มรอบเก็บตก would read as the 14-day rule.
  termDateErrorMessage(controlName: TermDateControlName): string {
    const errors = this.termForm.controls[controlName].errors;
    if (errors == null) {
      return '';
    }

    if (typeof errors['server'] === 'string') {
      return errors['server'];
    }

    // The datepicker's own VALIDATOR.REQUIRED copy is no longer rendered inline, so the
    // required case has to carry a message of its own here.
    if (errors['required'] === true) {
      return 'กรุณาระบุวันที่';
    }

    if (errors['pastDate'] === true) {
      return 'ตั้งวันที่ส่งเบิกย้อนหลังไม่ได้';
    }

    if (errors['catchUpGap'] === true) {
      return `ต้องเป็นวันหลังวันที่ส่งเบิกอย่างน้อย ${CATCH_UP_MIN_GAP_DAYS} วัน`;
    }

    if (errors['endDateOrder'] === true) {
      return 'ต้องเป็นวันหลังวันที่ส่งเบิก';
    }

    return 'ระบุวันที่ไม่ถูกต้อง';
  }

  // Rendered as one list under the save button instead of inline under each input: an
  // inline message made only that column taller and pushed the rest of the row out of line.
  // Same touched gate the inline messages used — a term with no วันที่ส่งเบิก yet is
  // `required`-invalid from the start and must not open on a red block.
  termDateErrors(): { label: string; message: string }[] {
    return TERM_DATE_LABELS
      .filter(({ control }) => {
        const field = this.termForm.controls[control];
        return field.invalid && field.touched;
      })
      .map(({ control, label }) => ({ label, message: this.termDateErrorMessage(control) }));
  }

  // Same shape as termDateErrorMessage: the shared inputs render "VALIDATOR.REQUIRED" as-is
  // (no i18n bundle in this app), so every required message is spelled out here.
  settingErrorMessage(controlName: SettingControlName): string {
    const control = this.settingForm.controls[controlName];
    if (control.errors == null) {
      return '';
    }

    return SETTING_REQUIRED_MESSAGES[controlName];
  }

  settingControlHasError(controlName: SettingControlName): boolean {
    const control = this.settingForm.controls[controlName];
    return control.invalid && control.touched;
  }

  // Backend-owned lock, always about the SELECTED term (ADR-DF) — context/decisions/DECISION_DisbursementV20.md
  isRowLocked(row: DepartmentSettingTableRow): boolean {
    return this.deptSettingsLocked() && row.raw.setting != null;
  }

  // Own value first, faculty default as the starting point for anything still blank —
  // a field the department never filled comes back as '', not null, so `??` is not enough.
  onOpenEditDialog(row: DepartmentSettingTableRow): void {
    if (this.isRowLocked(row)) {
      return;
    }

    const setting = row.raw.setting;
    const defaults = this.defaultSetting();
    this.editTarget.set(row);
    this.settingForm.reset({
      planName: this.prefillValue(setting?.plan_name, defaults?.plan_name),
      projectName: this.prefillValue(setting?.project_name, defaults?.project_name),
      expenseCategory: this.prefillValue(setting?.expense_category, defaults?.expense_category),
      financeOfficerUserId: setting?.finance_officer_user_id ?? null,
    });

    this.editDialogOpen.set(true);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    this.refreshDataSource();
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.currentPage = 1;
    this.refreshDataSource();
  }

  onCloseEditDialog(): void {
    if (this.settingSaving()) {
      return;
    }

    this.editDialogOpen.set(false);
    this.editTarget.set(null);
  }

  onOpenSettingSaveConfirm(): void {
    const row = this.editTarget();
    if (row == null || this.settingSaving() || this.isRowLocked(row)) {
      return;
    }

    // Show the red fields instead of a confirm dialog the user cannot complete.
    if (this.settingForm.invalid) {
      this.settingForm.markAllAsTouched();
      return;
    }

    const { academicTerm, academicYear } = this.termForm.getRawValue();
    this.settingSaveConfirmMessage.set(
      `ต้องการบันทึกการตั้งค่าของสาขา ${row.departmentName} สำหรับเทอม ${academicTerm}/${academicYear} หรือไม่ ?`,
    );
    this.settingSaveConfirmDialog?.open();
  }

  onSaveSetting(): void {
    const row = this.editTarget();
    if (row == null || this.settingSaving() || this.isRowLocked(row)) {
      return;
    }

    if (this.settingForm.invalid) {
      this.settingForm.markAllAsTouched();
      return;
    }

    const academicTermId = this.termForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      return;
    }

    const value = this.settingForm.getRawValue();
    this.settingSaving.set(true);

    this.settingsService.updateDepartmentSetting(row.departmentId, academicTermId, {
      plan_name: String(value.planName ?? '').trim(),
      project_name: String(value.projectName ?? '').trim(),
      expense_category: String(value.expenseCategory ?? '').trim(),
      finance_officer_user_id: value.financeOfficerUserId,
    }).pipe(
      finalize(() => this.settingSaving.set(false)),
    ).subscribe({
      next: () => {
        this.settingSaveConfirmDialog?.close();
        this.editDialogOpen.set(false);
        this.editTarget.set(null);
        this.loadDepartmentSettings();
        this.showSuccess('บันทึกการตั้งค่าสาขาวิชาสำเร็จ', 'บันทึกการตั้งค่าสาขาวิชาเรียบร้อยแล้ว');
      },
      // The edit dialog itself stays open on error so the user can fix the field — only
      // the confirm layer on top of it closes.
      error: (err) => {
        this.settingSaveConfirmDialog?.close();
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'order',
        name: 'ลำดับ',
        sortable: false,
        draggable: false,
        resizeable: false,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        cellClass: 'text-center',
        headerClass: 'd-flex justify-content-center',
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: false,
        width: 160,
        minWidth: 140,
      },
      {
        prop: 'planName',
        name: 'แผนงาน',
        sortable: false,
        width: 150,
        minWidth: 130,
      },
      {
        prop: 'projectName',
        name: 'งาน/โครงการ',
        sortable: false,
        width: 150,
        minWidth: 130,
      },
      {
        prop: 'expenseCategory',
        name: 'หมวดรายจ่าย',
        sortable: false,
        width: 170,
        minWidth: 140,
      },
      {
        prop: 'financeOfficerName',
        name: 'เจ้าหน้าที่การเงิน',
        sortable: false,
        width: 170,
        minWidth: 150,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'text-center',
        headerClass: 'text-center',
      },
    ];
  }

  private listenTermFilters(): void {
    this.subscriptions.add(
      this.termForm.controls.academicYear.valueChanges.subscribe(() => this.onAcademicYearChanged()),
    );
    this.subscriptions.add(
      this.termForm.controls.academicTerm.valueChanges.subscribe(() => {
        this.syncAcademicTermId();
        this.loadSelectedTerm();
      }),
    );
  }

  private onAcademicYearChanged(): void {
    const { academicYear, academicTerm } = this.termForm.getRawValue();
    this.academicTermNameOptions = buildAcademicTermNameOptions(this.academicTerms, academicYear);
    const nextTerm = academicTerm != null && this.academicTermNameOptions.includes(academicTerm)
      ? academicTerm
      : this.academicTermNameOptions[0] ?? null;
    this.termForm.patchValue({ academicTerm: nextTerm }, { emitEvent: false });
    this.syncAcademicTermId();
    this.loadSelectedTerm();
  }

  private syncAcademicTermId(): void {
    const { academicTerm, academicYear } = this.termForm.getRawValue();
    const resolved = findAcademicTerm(this.academicTerms, academicTerm, academicYear);
    this.termForm.patchValue({ academicTermId: resolved?.id ?? null }, { emitEvent: false });
  }

  private loadFilterOptions(): void {
    this.studyPeriodService.getAcademicTermYears().pipe(take(1)).subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.academicYearOptions = buildAcademicYearOptions(terms);
        const defaultTerm = currentAcademicTerm(terms, this.authService.currentUser()?.academic_term);
        const defaultYear = defaultTerm != null ? String(toBuddhistYear(defaultTerm.year)) : null;
        this.academicTermNameOptions = buildAcademicTermNameOptions(terms, defaultYear);
        this.termForm.patchValue(
          {
            academicYear: defaultYear,
            academicTerm: defaultTerm != null ? String(defaultTerm.name).trim() : null,
            academicTermId: defaultTerm?.id ?? null,
          },
          { emitEvent: false },
        );
        this.loadSelectedTerm();
      },
      error: (err) => console.error(err),
    });
  }

  // ทั้งวันที่ของเทอมและตารางสาขาเป็นข้อมูลของเทอมเดียวกัน — เปลี่ยนเทอมต้องโหลดใหม่ทั้งคู่
  private loadSelectedTerm(): void {
    this.loadTermSetting();
    this.loadDepartmentSettings();
  }

  private loadTermSetting(): void {
    const academicTermId = this.termForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.applyTermSetting(null);
      return;
    }

    this.termSettingLoading.set(true);
    this.settingsService.getTermSetting(academicTermId).pipe(
      take(1),
      finalize(() => this.termSettingLoading.set(false)),
    ).subscribe({
      next: (setting) => this.applyTermSetting(setting),
      error: (err) => {
        console.error(err);
        this.applyTermSetting(null);
      },
    });
  }

  private applyTermSetting(setting: DisbursementTermSettingModel | null): void {
    this.termSetting.set(setting);
    this.termForm.patchValue({
      disbursementDate: setting?.disbursement_date ?? null,
      catchUpDate: setting?.catch_up_date ?? null,
      endDate: setting?.end_date ?? null,
    }, { emitEvent: false });

    this.toggleControl(this.termForm.controls.disbursementDate, !this.termSettingLocked);
    this.toggleControl(this.termForm.controls.catchUpDate, !this.catchUpLocked);
  }

  private applyDefaultSetting(setting: DisbursementDefaultSettingModel | null): void {
    this.defaultSetting.set(setting);
    this.defaultForm.patchValue({
      planName: setting?.plan_name ?? '',
      projectName: setting?.project_name ?? '',
      expenseCategory: setting?.expense_category ?? '',
    }, { emitEvent: false });
  }

  private reseedLockedDates(): void {
    const setting = this.termSetting();
    if (setting == null) {
      return;
    }

    if (this.termSettingLocked) {
      this.termForm.controls.disbursementDate.setValue(setting.disbursement_date, { emitEvent: false });
    }

    if (this.catchUpLocked) {
      this.termForm.controls.catchUpDate.setValue(setting.catch_up_date, { emitEvent: false });
    }
  }

  // Angular reports a DISABLED control as valid, so an error placed on a locked date would
  // never render — those stay on the toast alone.
  private applyTermErrorMessages(err: unknown): void {
    this.extractErrorMessages(err).forEach((message) => {
      const field = TERM_DATE_ERROR_PREFIXES.find((entry) => message.startsWith(entry.prefix));
      if (field == null) {
        return;
      }

      const control = this.termForm.controls[field.control];
      if (control.disabled) {
        return;
      }

      control.setErrors({ server: message.slice(field.prefix.length).trim() });
      control.markAsTouched();
    });
  }

  private toggleControl(control: AbstractControl, enabled: boolean): void {
    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private showSuccess(title: string, message: string): void {
    this.successTitle.set(title);
    this.successMessage.set(message);
    this.actionSuccessDialog?.open();
  }

  private loadDepartmentSettings(): void {
    const academicTermId = this.termForm.getRawValue().academicTermId;
    if (academicTermId == null) {
      this.planRows = [];
      this.deptSettingsLocked.set(false);
      this.currentPage = 1;
      this.rebuildRows();
      return;
    }

    this.tableLoading.set(true);
    this.settingsService.getDepartmentSettings(academicTermId).pipe(
      take(1),
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.planRows = response.data;
        this.deptSettingsLocked.set(response.locked === true);
        this.applyDefaultSetting(response.defaults ?? null);
        this.currentPage = 1;
        this.rebuildRows();
      },
      error: (err) => {
        console.error(err);
        this.planRows = [];
        this.deptSettingsLocked.set(false);
        this.currentPage = 1;
        this.rebuildRows();
      },
    });
  }

  private rebuildRows(): void {
    const rows = this.planRows.map((row) => this.mapRow(row));
    this.datasource.set(this.createDataSource(rows, this.currentPage, this.currentLimit));
  }

  private refreshDataSource(): void {
    this.rebuildRows();
  }

  private loadFinanceOfficers(): void {
    this.settingsService.getFinanceOfficers().pipe(take(1)).subscribe({
      next: (response) => this.financeOfficers.set(response.data),
      error: (err) => {
        console.error(err);
        this.financeOfficers.set([]);
      },
    });
  }

  // Only what the department saved itself — the faculty defaults never show up here,
  // so a row reads exactly as what its ใบปะหน้า will print.
  private mapRow(row: DepartmentDisbursementSettingRow): DepartmentSettingTableRow {
    const setting = row.setting;

    return {
      departmentId: row.department.id,
      departmentName: row.department.name_th ?? row.department.name ?? row.department.name_en ?? '-',
      planName: this.displayValue(setting?.plan_name),
      projectName: this.displayValue(setting?.project_name),
      expenseCategory: this.displayValue(setting?.expense_category),
      financeOfficerName: this.displayValue(setting?.finance_officer?.full_name),
      raw: row,
    };
  }

  private displayValue(value?: string | null): string {
    return value != null && value !== '' ? value : '-';
  }

  private prefillValue(own?: string | null, fallback?: string | null): string {
    const value = own ?? '';
    return value !== '' ? value : fallback ?? '';
  }

  // The backend returns the dept list whole (unpaginated) — paginate client-side
  // for the same table UX as cover-memos/registry.
  private createDataSource(
    rows: DepartmentSettingTableRow[],
    page = 1,
    limit = 50,
  ): DepartmentSettingDataSource {
    const total = rows.length;
    const totalPage = Math.max(Math.ceil(total / limit), 1);
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);
    const offset = (normalizedPage - 1) * limit;

    return {
      data: rows.slice(offset, offset + limit),
      limit,
      offset,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  // Compare on real calendar days: the datepicker serialises to a full JS Date
  // string ("Fri Aug 14 2026 …"), so a naive string compare mis-orders dates
  // across month boundaries (14/08 would sort before 25/07 on the weekday prefix).
  private catchUpGapSatisfied(disbursementDate: string, catchUpDate: string): boolean {
    const start = this.toCalendarDay(disbursementDate);
    const catchUp = this.toCalendarDay(catchUpDate);
    if (start == null || catchUp == null) {
      return true; // unparseable — let the backend adjudicate
    }

    const gapDays = Math.round((catchUp - start) / (24 * 60 * 60 * 1000));
    return gapDays >= CATCH_UP_MIN_GAP_DAYS;
  }

  private endDateAfterDisbursement(disbursementDate: string, endDate: string): boolean {
    const start = this.toCalendarDay(disbursementDate);
    const end = this.toCalendarDay(endDate);
    if (start == null || end == null) {
      return true; // unparseable — let the backend adjudicate
    }

    return end > start;
  }

  private isPastDay(value: string): boolean {
    const day = this.toCalendarDay(value);
    if (day == null) {
      return false; // unparseable — let the backend adjudicate
    }

    return day < this.todayCalendarDay();
  }

  private todayCalendarDay(): number {
    const now = new Date();
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private toCalendarDay(value: string): number | null {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  private extractErrorMessage(err: unknown): string {
    return this.extractErrorMessages(err)[0] ?? 'ไม่สามารถดำเนินการได้';
  }

  private extractErrorMessages(err: unknown): string[] {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors)) {
          return errors.map((message) => String(message));
        }
        if (typeof errors === 'string') {
          return [errors];
        }
      }
    }

    return [];
  }
}
